import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollText, TrendingUp, Wrench, FileText, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const TYPE_META: Record<string, { label: string; icon: typeof ScrollText }> = {
  "terms-update": { label: "Actualizare T&C", icon: ScrollText },
  "price-change": { label: "Modificare prețuri", icon: TrendingUp },
  "service-announcement": { label: "Anunț de serviciu", icon: Wrench },
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const EmailCampaignsHistory = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-email-campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_email_campaigns")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-semibold">Istoric notificări trimise (audit GDPR)</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Înregistrare imuabilă a tuturor notificărilor în masă — utilă pentru control ANSPDCP.
        Ultimele 100 de campanii.
      </p>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Se încarcă istoricul...
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">
          Eroare la încărcare: {error instanceof Error ? error.message : "necunoscută"}
        </p>
      )}

      {!isLoading && data && data.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">
          Nici o notificare în masă încă.
        </p>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((row) => {
            const meta = TYPE_META[row.campaign_type] ?? {
              label: row.campaign_type,
              icon: FileText,
            };
            const Icon = meta.icon;
            const hasErrors = (row.error_count ?? 0) > 0;
            return (
              <div
                key={row.id}
                className="flex flex-wrap items-start gap-3 rounded-lg border border-border bg-background p-3 text-sm"
              >
                <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{meta.label}</span>
                    {row.campaign_type === "service-announcement" && (() => {
                      const sev = (row.payload as { severity?: string } | null)?.severity ?? "info";
                      const sevMeta: Record<string, { label: string; emoji: string; className: string }> = {
                        info: { label: "Informare", emoji: "ℹ️", className: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30" },
                        warning: { label: "Atenție", emoji: "⚠️", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
                        critical: { label: "Urgent", emoji: "🚨", className: "bg-destructive/15 text-destructive border-destructive/30" },
                      };
                      const m = sevMeta[sev] ?? sevMeta.info;
                      return (
                        <Badge variant="outline" className={m.className}>
                          {m.emoji} {m.label}
                        </Badge>
                      );
                    })()}
                    <Badge variant={hasErrors ? "destructive" : "secondary"}>
                      {row.success_count}/{row.recipient_count} trimise
                    </Badge>
                    {hasErrors && (
                      <Badge variant="destructive">{row.error_count} eșuate</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(row.created_at)} · de {row.sent_by_email ?? row.sent_by_user_id}
                  </p>
                  {row.payload && typeof row.payload === "object" && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        Vezi detaliile trimise
                      </summary>
                      <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(row.payload, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default EmailCampaignsHistory;
